import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  HouseSimple,
  Sparkle,
  User as UserIcon,
  Vault,
  VideoCamera,
  SignOut,
  Shield,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import SidebarAktivAlap from "./SidebarAktivAlap";

const navItems = [
  { to: "/dashboard", label: "Műhely", icon: HouseSimple, testid: "nav-studio" },
  { to: "/agents", label: "Ügynökök", icon: Sparkle, testid: "nav-agents" },
  { to: "/identity", label: "Identitás", icon: UserIcon, testid: "nav-identity" },
  { to: "/asset-vault", label: "Tudástár", icon: Vault, testid: "nav-vault" },
  { to: "/recordings", label: "Felvételek", icon: VideoCamera, testid: "nav-recordings" },
];

const adminNavItem = {
  to: "/admin",
  label: "Admin pult",
  icon: Shield,
  testid: "nav-admin",
};

export default function Sidebar() {
  const { user, logout, effectiveRole } = useAuth();
  const location = useLocation();
  // Admin link csak akkor látszik, ha (1) a user valóban admin ÉS
  // (2) NEM Tester nézet előnézet módban van. Preview módban
  // pontosan azt látja a sidebarban, amit egy tester látna.
  const isAdmin = effectiveRole === "admin";
  const items = isAdmin ? [...navItems, adminNavItem] : navItems;

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex flex-col w-[220px] shrink-0 h-screen border-r border-white/[0.06] bg-[#0b0b12] sticky top-0"
    >
      <div className="px-6 pt-7 pb-6">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
          <div className="relative w-8 h-8 rounded-lg bg-gradient-to-br from-[#5e50f7] to-[#3a2fd3] flex items-center justify-center shadow-[0_4px_20px_rgba(94,80,247,0.45)]">
            <Sparkle size={18} weight="fill" color="#fff" />
          </div>
          <div className="font-heading text-[19px] tracking-tight">
            MEX <span className="text-[#5e50f7]">Magic</span>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 space-y-1">
        <div className="px-3 py-2 mex-eyebrow">Menü</div>
        {items.map((item) => {
          const Icon = item.icon;
          const active =
            location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-all ${
                active
                  ? "bg-[#5e50f7]/10 text-white border border-[#5e50f7]/30"
                  : "text-[#a1a1a1] hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <Icon size={18} weight={active ? "fill" : "regular"} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Aktív alap — Foundation indicator, sits above the profile block */}
      <SidebarAktivAlap />

      <div className="p-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 p-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-9 h-9 rounded-full object-cover border border-white/10"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center text-[13px]">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] truncate" data-testid="sidebar-username">
              {user?.name || "Vendég"}
            </div>
            <div className="text-[11px] text-[#a1a1a1] truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="p-2 rounded-md text-[#a1a1a1] hover:text-white hover:bg-white/[0.04] transition"
            aria-label="Kijelentkezés"
          >
            <SignOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
